import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Radar, MailCheck, ArrowRight, RefreshCw, LogOut } from "lucide-react";
import { Button } from "../components/ui/button";
import { InputOTP, InputOTPGroup, InputOTPSlot } from "../components/ui/input-otp";
import { useAuth } from "../context/AuthContext";
import { useToast } from "../hooks/use-toast";
import api from "../lib/api";

const redirectFor = (role) => (role === "admin" ? "/dashboard" : role === "client" ? "/client" : "/employee");

const VerifyEmail = () => {
  const navigate = useNavigate();
  const { user, updateUser, logout } = useAuth();
  const { toast } = useToast();
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [cooldown, setCooldown] = useState(0);

  useEffect(() => {
    if (!user) { navigate("/"); return; }
    if (user.email_verified) { navigate(redirectFor(user.role)); return; }
    // eslint-disable-next-line
  }, [user]);

  useEffect(() => {
    if (cooldown <= 0) return;
    const t = setTimeout(() => setCooldown((c) => c - 1), 1000);
    return () => clearTimeout(t);
  }, [cooldown]);

  const sendCode = async (silent = false) => {
    try {
      await api.post("/auth/send-code", { email: user.email });
      setCooldown(30);
      if (!silent) toast({ title: "Code sent", description: `We emailed a 6-digit code to ${user.email}` });
    } catch (e) { /* ignore */ }
  };

  // auto-send a code when landing here
  useEffect(() => {
    if (user && !user.email_verified) sendCode(true);
    // eslint-disable-next-line
  }, []);

  const verify = async (value) => {
    const c = value || code;
    if (c.length !== 6) return;
    setBusy(true);
    try {
      await api.post("/auth/verify-code", { email: user.email, code: c });
      updateUser({ email_verified: true });
      toast({ title: "Email verified!", description: "Your account is now active." });
      navigate(redirectFor(user.role));
    } catch (e) {
      toast({ title: "Verification failed", description: e?.response?.data?.detail || "Invalid code.", variant: "destructive" });
      setCode("");
    } finally { setBusy(false); }
  };

  if (!user) return null;

  return (
    <div className="flex min-h-screen items-center justify-center bg-slate-50/60 px-6 py-12">
      <div className="w-full max-w-md">
        <div className="mb-8 flex items-center justify-center gap-2.5">
          <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-[#2f2b8f]"><Radar className="h-5 w-5 text-white" /></div>
          <span className="text-lg font-bold text-[#1a1a2e]">Files on Radar</span>
        </div>
        <div className="rounded-2xl border border-slate-100 bg-white p-8 text-center shadow-sm">
          <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-2xl bg-indigo-50"><MailCheck className="h-7 w-7 text-indigo-700" /></div>
          <h1 className="text-2xl font-bold text-[#1a1a2e]">Verify your email</h1>
          <p className="mt-2 text-sm text-slate-500">
            We've sent a 6-digit verification code to<br /><span className="font-semibold text-[#1a1a2e]">{user.email}</span>. Enter it below to activate your account.
          </p>

          <div className="mt-6 flex justify-center">
            <InputOTP maxLength={6} value={code} onChange={(v) => { setCode(v); if (v.length === 6) verify(v); }}>
              <InputOTPGroup>
                <InputOTPSlot index={0} />
                <InputOTPSlot index={1} />
                <InputOTPSlot index={2} />
                <InputOTPSlot index={3} />
                <InputOTPSlot index={4} />
                <InputOTPSlot index={5} />
              </InputOTPGroup>
            </InputOTP>
          </div>

          <Button onClick={() => verify()} disabled={busy || code.length !== 6} className="group mt-6 w-full rounded-xl bg-[#2f2b8f] py-6 text-sm font-semibold text-white hover:bg-[#26236f]">
            {busy ? "Verifying..." : "Verify & continue"}
            <ArrowRight className="ml-2 h-4 w-4 transition-transform group-hover:translate-x-1" />
          </Button>

          <div className="mt-4 text-sm text-slate-500">
            Didn't get the code?{" "}
            <button disabled={cooldown > 0} onClick={() => sendCode(false)} className="inline-flex items-center gap-1 font-medium text-[#2f2b8f] hover:underline disabled:opacity-50 disabled:no-underline">
              <RefreshCw className="h-3.5 w-3.5" /> {cooldown > 0 ? `Resend in ${cooldown}s` : "Resend code"}
            </button>
          </div>
          <p className="mt-2 text-xs text-slate-400">The code expires in 10 minutes.</p>

          <button onClick={() => { logout(); navigate("/"); }} className="mt-6 inline-flex items-center gap-1.5 text-xs font-medium text-slate-400 hover:text-slate-600">
            <LogOut className="h-3.5 w-3.5" /> Use a different account
          </button>
        </div>
      </div>
    </div>
  );
};

export default VerifyEmail;
